import React from 'react';
import { Star, Building2, Calendar, User, Image as ImageIcon } from 'lucide-react';
import DOMPurify from 'dompurify';
import { PromotionalContent, ENTITY_TYPE_CONFIG } from '../types/promotional-content';
import { formatDate } from '../utils/dateUtils';

interface PromotionalContentDisplayProps {
  content: PromotionalContent[];
  entityName: string;
  entityType: keyof typeof ENTITY_TYPE_CONFIG;
  showOrganizationAttribution?: boolean;
  maxItems?: number;
  className?: string;
}

const PromotionalContentDisplay: React.FC<PromotionalContentDisplayProps> = ({
  content,
  entityName,
  entityType,
  showOrganizationAttribution = true,
  maxItems,
  className = ''
}) => {
  if (!content || content.length === 0) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <div className="text-neutral-500 dark:text-neutral-500 mb-2">
          <Building2 className="w-8 h-8 mx-auto" />
        </div>
        <p className="text-neutral-500 dark:text-text-dark-tertiary text-sm">
          No promotional content available for this {ENTITY_TYPE_CONFIG[entityType].displayName.toLowerCase()}.
        </p>
      </div>
    );
  }

  // Sort content: featured first, then by display order, then by creation date
  const sortedContent = [...content].sort((a, b) => {
    if (a.is_featured !== b.is_featured) {
      return a.is_featured ? -1 : 1;
    }
    if (a.display_order !== b.display_order) {
      return a.display_order - b.display_order;
    }
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  // Limit items if specified
  const displayContent = maxItems ? sortedContent.slice(0, maxItems) : sortedContent;


  // Sanitize HTML content for safe rendering
  const sanitizeHTML = (html: string) => {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'ul', 'ol', 'li', 'a'],
      ALLOWED_ATTR: ['href', 'target', 'rel'],
      ALLOW_DATA_ATTR: false
    });
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {displayContent.map((item) => (
        <div
          key={item.id}
          className={`
            card p-0 overflow-hidden
            ${item.is_featured 
              ? 'border-primary-200 dark:border-primary-700 ring-1 ring-primary-100 dark:ring-primary-800' 
              : ''
            }
          `}
        >
          {/* Featured Badge */}
          {item.is_featured && (
            <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-4 py-2">
              <div className="flex items-center text-white text-sm font-medium">
                <Star className="w-4 h-4 mr-2 fill-current" />
                Featured Content
              </div>
            </div>
          )}

          <div className="p-6">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                {item.title && (
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">
                    {item.title}
                  </h3>
                )}
                
                {showOrganizationAttribution && (
                  <div className="flex items-center text-sm text-neutral-600 dark:text-text-dark-tertiary mb-3">
                    <Building2 className="w-4 h-4 mr-1" />
                    <span className="font-medium">{item.organization_name}</span>
                    {item.created_by_name && (
                      <>
                        <span className="mx-2">•</span>
                        <User className="w-4 h-4 mr-1" />
                        <span>by {item.created_by_name}</span>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Image */}
              {item.image_url && (
                <div className="ml-4 flex-shrink-0">
                  <img
                    src={item.image_url}
                    alt={item.title || `${entityName} promotional image`}
                    className="w-24 h-24 object-cover rounded-lg shadow-md"
                    onError={(e) => {
                      // Hide image if it fails to load
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>

            {/* Content */}
            <div 
              className="prose prose-sm max-w-none dark:prose-invert text-neutral-700 dark:text-text-dark-secondary leading-relaxed"
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(item.summary) }}
            />

            {/* Footer */}
            <div className="mt-4 pt-4 border-t border-border-light dark:border-border-dark-hairline">
              <div className="flex items-center justify-between text-xs text-neutral-500 dark:text-text-dark-tertiary">
                <div className="flex items-center">
                  <Calendar className="w-3 h-3 mr-1" />
                  <span>Published {formatDate(item.created_at)}</span>
                  {item.updated_at !== item.created_at && (
                    <span className="ml-2">• Updated {formatDate(item.updated_at)}</span>
                  )}
                </div>
                
                {item.image_url && (
                  <div className="flex items-center">
                    <ImageIcon className="w-3 h-3 mr-1" />
                    <span>Image included</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Show more indicator */}
      {maxItems && content.length > maxItems && (
        <div className="text-center py-4">
          <p className="text-sm text-neutral-500 dark:text-text-dark-tertiary">
            Showing {maxItems} of {content.length} promotional content items
          </p>
        </div>
      )}
    </div>
  );
};

export default PromotionalContentDisplay;
